import { Minus, Plus } from '@teo/components/icons';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

function toMS(totalMs) {
  const sec = Math.floor((totalMs || 0) / 1000);
  return { minutes: Math.floor(sec / 60), seconds: sec % 60 };
}

function fromMS(m, s) {
  return (m * 60 + s) * 1000;
}

function MMSSInput({ value, onChange }) {
  const { minutes, seconds } = useMemo(() => toMS(value), [value]);
  const [segment, setSegment] = useState('m');
  const [buffer, setBuffer] = useState('');
  const inputRef = useRef(null);

  const valueString = `${two(minutes)}:${two(seconds)}`;

  // сегментні позиції
  const ranges = {
    m: { start: 0, end: 2 },
    s: { start: 3, end: 5 },
  };

  function two(n) {
    return n.toString().padStart(2, '0');
  }

  function apply(seg, next) {
    next = Math.max(0, Math.min(next, 59));
    if (seg === 'm') onChange(fromMS(next, seconds));
    if (seg === 's') onChange(fromMS(minutes, next));
  }

  function handleClick(e) {
    const pos = e.currentTarget.selectionStart ?? 0;
    if (pos <= 2) setSegment('m');
    else setSegment('s');
  }

  function handleKeyDown(e) {
    const key = e.key;
    if (/^[0-9]$/.test(key)) {
      e.preventDefault();
      const nextBuf = (buffer + key).slice(-2);
      setBuffer(nextBuf);
      apply(segment, Number(nextBuf));

      if (nextBuf.length === 2) {
        setBuffer('');
        setSegment(segment === 'm' ? 's' : 'm');
      }
      return;
    }
    if (key === 'ArrowUp') {
      e.preventDefault();
      apply(segment, (segment === 'm' ? minutes : seconds) + 1);
    }
    if (key === 'ArrowDown') {
      e.preventDefault();
      apply(segment, (segment === 'm' ? minutes : seconds) - 1);
    }
    if (key === 'ArrowLeft' || (key === 'Tab' && e.shiftKey)) {
      e.preventDefault();
      setSegment(segment === 's' ? 'm' : 'm');
    }
    if (key === 'ArrowRight' || key === 'Tab') {
      e.preventDefault();
      setSegment(segment === 'm' ? 's' : 's');
    }
    if (key === 'Backspace') {
      e.preventDefault();
      setBuffer('');
      apply(segment, Math.floor((segment === 'm' ? minutes : seconds) / 10));
    }
    if (key === ':') {
      e.preventDefault();
      setSegment('s');
    }
  }

  useEffect(() => {
    const el = inputRef.current;
    if (!el) return;
    const r = ranges[segment];
    el.setSelectionRange(r.end, r.end);
  }, [segment, valueString]);

  return (
    <input
      ref={inputRef}
      value={valueString}
      onClick={handleClick}
      onKeyDown={handleKeyDown}
      //   readOnly
      className="w-[80px] rounded-lg bg-grey-01 px-2 py-2 text-center text-base text-grey-08"
    />
  );
}

export default function TimeControl({ start, end, onChange }) {
  const STEP = 1000;

  const [startValue, setStartValue] = useState(start);
  const [endValue, setEndtValue] = useState(end);
  const [dur, setDur] = useState(Math.max(0, end - start));

  const { t } = useTranslation();

  const updateStart = (e) => {
    setStartValue(e);
    onChange({ start: e, end });
    setDur(Math.max(0, endValue - e));
  };
  const updateEnd = (e) => {
    setEndtValue(e);
    onChange({ start, end: e });
    setDur(Math.max(0, e - startValue));
  };

  const updateDuration = (delta) => {
    const newEnd = Math.max(startValue + 1000, endValue + delta);
    setEndtValue(newEnd);
    setDur(Math.max(0, newEnd - startValue));
    onChange({ start, end: newEnd });
  };

  return (
    <div className="flex w-auto items-center gap-6 text-sm text-grey-07">
      <div className="flex items-center gap-2">
        <div className="text-black">
          {t('pages.create_edit_course.video_instruction.start_time')}
        </div>
        <MMSSInput value={startValue} onChange={updateStart} />
      </div>

      <div className="flex items-center gap-2">
        <div className="text-black">
          {t('pages.create_edit_course.video_instruction.end_time')}
        </div>
        <MMSSInput value={endValue} onChange={updateEnd} />
      </div>

      {/* DURATION */}
      <div className="flex items-center gap-2 border-l pl-6">
        <div className="mr-2 text-black">
          {t('pages.create_edit_course.video_instruction.duration')}
        </div>
        <button
          type="button"
          className="rounded-md border p-[7px]"
          onClick={() => updateDuration(-STEP)}
        >
          <Minus className="h-6 w-6" />
        </button>

        <MMSSInput value={dur} onChange={setDur} />

        <button
          type="button"
          className="rounded-md border p-[7px]"
          onClick={() => updateDuration(+STEP)}
        >
          <Plus className="h-6 w-6" />
        </button>
      </div>
    </div>
  );
}
